import { useMemo } from 'react';
import { Pin, PinOff, UserMinus } from 'lucide-react';
import type { SeatingParty, SeatingTable } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { freeSeats, partiesAt, tableTitle } from './boardModel';

const NOT_SEATED = '__not_seated__';

export interface PartySheetProps {
  party: SeatingParty | null;
  tables: SeatingTable[];
  parties: Map<string, SeatingParty>;
  canWrite: boolean;
  busy?: boolean;
  onClose: () => void;
  onMove: (tableUid: string | null) => void;
  onSwap: (otherGuestId: string) => void;
  /** Pin or unpin the party's seats; absent where pinning is not offered (the portal). */
  onPin?: (locked: boolean) => void;
  onMoveMember: (partyMemberId: string, tableUid: string | null) => void;
}

/**
 * A seated party's actions — what a tap on it opens, on a phone as a sheet
 * from the bottom: move it, swap it with another party, take it off the plan,
 * pin it so auto-assign leaves it be, and seat its named people one by one.
 */
export function PartySheet({
  party,
  tables,
  parties,
  canWrite,
  busy = false,
  onClose,
  onMove,
  onSwap,
  onPin,
  onMoveMember,
}: PartySheetProps) {
  const where = useMemo(() => {
    if (!party) return [];
    return tables.flatMap((table) => {
      const here = partiesAt(table, parties).find((p) => p.guestId === party.guestId);
      return here ? [{ table, seats: here.seats, locked: here.locked }] : [];
    });
  }, [party, tables, parties]);
  const pinned = where.some((w) => w.locked);

  const swapWith = useMemo(() => {
    if (!party) return [];
    const mine = new Set(party.tableUids);
    return tables
      .filter((table) => !mine.has(table.uid))
      .flatMap((table) =>
        partiesAt(table, parties).map((p) => ({ guestId: p.guestId, name: p.name, table })),
      );
  }, [party, tables, parties]);

  const memberSeat = (memberId: string) => {
    for (const table of tables) {
      const seat = table.occupied.find((s) => s.partyMemberId === memberId);
      if (seat) return { table, seat: seat.seatNumber };
    }
    return null;
  };

  const tableOptions = [
    { value: NOT_SEATED, label: 'Not seated' },
    ...tables.map((table) => ({
      value: table.uid,
      label: `${tableTitle(table)} · ${freeSeats(table)} free`,
    })),
  ];

  return (
    <EntityModal open={!!party} onClose={onClose} title={party?.name ?? 'Party'} size="sm">
      {party && (
        <div className="space-y-4" data-testid="party-sheet">
          <p className="text-sm text-gray-600">
            Party of {party.partySize}
            {where.length > 0
              ? ` · ${where.map((w) => `${tableTitle(w.table)}, seat${w.seats.length === 1 ? '' : 's'} ${w.seats.join(', ')}`).join('; ')}`
              : ' · not seated'}
          </p>

          {canWrite && (
            <>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Move to</span>
                <SearchableListSelect
                  items={tables}
                  value={null}
                  onChange={(value) => onMove(value as string)}
                  getValue={(table) => table.uid}
                  getLabel={(table) => tableTitle(table)}
                  getSubtitle={(table) => `${freeSeats(table)} of ${table.seats} free`}
                  placeholder="Choose a table"
                  ariaLabel="Move to table"
                  disabled={busy}
                />
              </label>

              {swapWith.length > 0 && (
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Swap with</span>
                  <SearchableListSelect
                    items={swapWith}
                    value={null}
                    onChange={(value) => onSwap(value as string)}
                    getValue={(item) => item.guestId}
                    getLabel={(item) => item.name}
                    getSubtitle={(item) => tableTitle(item.table)}
                    placeholder="Choose a party at another table"
                    ariaLabel="Swap with"
                    disabled={busy}
                  />
                </label>
              )}

              {party.members.length > 0 && (
                <div>
                  <span className="block text-xs font-medium text-gray-700 mb-1">Named people</span>
                  <ul className="space-y-2" data-testid="party-members">
                    {party.members.map((member) => {
                      const at = memberSeat(member.id);
                      return (
                        <li key={member.id} className="flex items-center gap-2">
                          <span className="w-32 shrink-0 truncate text-sm text-gray-900">
                            {member.name}
                          </span>
                          <div className="flex-1">
                            <SearchableListSelect
                              items={tableOptions}
                              value={at ? at.table.uid : NOT_SEATED}
                              onChange={(value) =>
                                onMoveMember(
                                  member.id,
                                  value === NOT_SEATED ? null : (value as string),
                                )
                              }
                              getValue={(option) => option.value}
                              getLabel={(option) => option.label}
                              ariaLabel={`Seat ${member.name}`}
                              disabled={busy}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                {where.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onMove(null)}
                    disabled={busy}
                    className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <UserMinus size={14} /> Unseat
                  </button>
                )}
                {where.length > 0 && onPin && (
                  <button
                    type="button"
                    onClick={() => onPin(!pinned)}
                    disabled={busy}
                    aria-pressed={pinned}
                    className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {pinned ? <PinOff size={14} /> : <Pin size={14} />}
                    {pinned ? 'Unpin' : 'Pin — auto-assign leaves it'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </EntityModal>
  );
}
